// Module: PracticeDiagramFrame
// -----------------------------------------------------------------
// Receives:  a diagram from practiceDiagramLoader (the host export as a
//            srcDoc), the caller's scale and transform origin
// Produces:  the diagram at deck size in an iframe, with its math hydrated
// Owns:      the render of one diagram
// Does NOT:  load the export (→ practiceDiagramLoader), size its container
//            (→ callers)
// -----------------------------------------------------------------
import { renderMathElements } from "@ever-tutor/et-ui";
import { SLIDE_HEIGHT, SLIDE_WIDTH } from "@/constants/slideDeck";
import { PracticeDiagram } from "./practiceDiagramLoader";

/**
 * A practice's host diagram, rendered like every other slide preview: in an
 * iframe, so the export's styles and fonts and the app's stay apart in both
 * directions.
 *
 * The stored export leaves inline math EMPTY (`data-type="mathInline"` spans
 * carry only `data-latex`, `.math-input` holds raw LaTeX), so on load the
 * parent hydrates it through `contentDocument` — reachable because the
 * srcDoc frame is same-origin. No `allow-scripts`: the editor export has none,
 * and with same-origin it would hand any script the app's origin.
 */
const PracticeDiagramFrame = ({
  diagram,
  scale,
  title,
  transformOrigin = "top left",
}: {
  diagram: PracticeDiagram;
  scale: number;
  title: string;
  transformOrigin?: "top left" | "center";
}) => (
  <iframe
    srcDoc={diagram.srcDoc}
    title={title}
    className="pointer-events-none border-0"
    scrolling="no"
    sandbox="allow-same-origin"
    onLoad={(event) => {
      const doc = event.currentTarget.contentDocument;
      if (doc) renderMathElements(doc);
    }}
    style={{
      width: SLIDE_WIDTH,
      height: SLIDE_HEIGHT,
      transform: `scale(${scale})`,
      transformOrigin,
      flex: "none",
    }}
  />
);

export default PracticeDiagramFrame;
